/**
 * 段階解禁ダッシュボード (Phase 9 簡易版)。
 *
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §13
 * - lastTouchedAt が 7日超の open Deal を一覧
 * - notifyStaleDeals Scheduler が毎朝同じデータを Google Chat に通知
 */
import type { DealStage } from '@goenhub/shared';
import { AlertTriangle } from 'lucide-react';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { useDeals } from '@/hooks/useDeals';

const STAGE_LABEL: Record<DealStage, string> = {
  document_requested: '資料請求',
  briefing: '個別説明',
  quote: '見積',
  won: '成約',
  agreed: '同意',
  paid: '入金',
  ordered: '発注',
  delivered: '納品',
  lost: '失注',
};

const STALE_DAYS = 7;

function tsToMs(v: unknown): number | null {
  if (!v) return null;
  if (typeof v === 'string') return new Date(v).getTime();
  if (typeof v === 'object' && v !== null && 'toMillis' in v) {
    return (v as { toMillis: () => number }).toMillis();
  }
  return null;
}

export function StaleDealsPage() {
  const { data: deals, isLoading } = useDeals();

  const stale = (deals ?? []).filter((d) => {
    if (d.status !== 'open') return false;
    const ms = tsToMs(d.lastTouchedAt);
    if (!ms) return false;
    return Date.now() - ms > STALE_DAYS * 86400000;
  });

  return (
    <Screen
      title="段階解禁ダッシュボード"
      description={`${STALE_DAYS}日以上接触のない open 商談を一覧表示。毎朝 09:00 に Google Chat に通知されます。`}
    >
      {isLoading ? (
        <Spinner />
      ) : stale.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title="停滞商談はありません"
          description="すべての商談が直近で更新されています。"
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border)] text-left">
                <th className="py-2 px-3">商談</th>
                <th className="py-2 px-3">ステージ</th>
                <th className="py-2 px-3">最終接触</th>
                <th className="py-2 px-3 text-right">経過日数</th>
              </tr>
            </thead>
            <tbody>
              {stale.map((d) => {
                const ms = tsToMs(d.lastTouchedAt);
                const days = ms ? Math.floor((Date.now() - ms) / 86400000) : 0;
                return (
                  <tr
                    key={d.id}
                    className="border-b border-[var(--border)] hover:bg-[var(--bg-hover)]"
                  >
                    <td className="py-2 px-3 font-medium">{d.title}</td>
                    <td className="py-2 px-3">
                      <Badge>{STAGE_LABEL[d.stage]}</Badge>
                    </td>
                    <td className="py-2 px-3">
                      {ms ? new Date(ms).toLocaleDateString('ja-JP') : '-'}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums">{days} 日</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Screen>
  );
}
